import dayjs from 'dayjs';
import { Content, TDocumentDefinitions, TableCell } from 'pdfmake/interfaces';
import { BaseAsset } from 'queries/assets/contract';
import { SeverityStatusColorMap } from './helpers';
import { getImageFromName } from '@shape/lighthouse-components';
import * as types from './types';
import { buildGauge } from './components/Gauge';
import { buildLegends, legendAsString } from './components/Legends';
import { buildProgress } from './components/Progress';
import {
  DividerLine,
  buildEquipmentSapNotificationLine,
  buildTemplateSapNotificationsPage,
} from './pages/tableSapNotifications';

const BLUE_BORDER = '#0058A9';
let placeholders: { [key: string]: string } = {
  cover: '',
  asset: '',
};

export const getPDFDiagnostic = (parameter: any, item: any) => {
  return parameter.diagnostic.length
    ? parameter.diagnostic.map((cd: any, i: number) => ({
        text: `${i + 1} - ${cd.name}`,
        linkToDestination: `${cd.name}-${parameter.name}-${i}-${item.name}`,
        fontSize: 7,
        color: 'blue',
        decoration: 'underline',
        margin: [0, 0, 0, 3],
      }))
    : {
        text: `${parameter.name} was normal during this period`,
        fontSize: 7,
        color: '#666666',
        margin: [0, 0, 0, 3],
      };
};

export const getDiagnosticsWithSapNotifications = (
  diagnostics: types.DeviationDetails[],
): types.DeviationDetails[] => {
  return diagnostics
    .filter((item) => item.sap_notification && item.sap_notification !== '0')
    .map((item) => {
      const specialCharacters = /[^\w\s]/gi;
      return { ...item, sap_notification: item.sap_notification.replace(specialCharacters, '') };
    });
};

export const deviationToPDF = async (
  data: types.HealthScoreEquipments,
  dateRange: [Date, Date],
  documentId: string,
  externalIdReferences?: Record<string, BaseAsset>,
): Promise<TDocumentDefinitions> => {
  placeholders = {
    cover: await getImageFromName('cover-placeholder.png'),
    asset: await getImageFromName('asset-placeholder.png'),
  };

  const footerImage = await getImageFromName('powered.png');
  const headerImage = await getImageFromName('header - dev.png');
  const frontPageImage = await getImageFromName(`Report-cover - ${data.name} - dev.png`, 'cover');

  const TABLE_HEADER_OBJ = (isFirstPage?: boolean) => {
    return JSON.stringify({
      columns: [
        {
          width: isFirstPage ? 110 : 110,
          text: 'Systems',
          bold: true,
          fontSize: 7,
          margin: [20, 5],
        },
        {
          width: isFirstPage ? 110 : 52,
          text: 'Health Score',
          bold: true,
          fontSize: 7,
          margin: isFirstPage ? [30, 5] : [0, 5],
        },
        !isFirstPage ? buildLegends() : null,
      ],
    });
  };

  const BORDER_GRAY = ['#E0E0E0', '#E0E0E0', '#E0E0E0', '#E0E0E0'];

  const result: TDocumentDefinitions = {
    pageMargins: [40, 70, 40, 60],
    footer: function (currentPage: number) {
      if (currentPage !== 1) {
        return {
          columns: [
            {
              image: footerImage,
              fit: [90, 38],
              alignment: 'center',
              margin: [0, 10],
            },
          ],
        };
      }
      return false;
    } as unknown as Content,
    header: function (currentPage: number) {
      if (currentPage !== 1) {
        return {
          stack: [
            {
              width: 600,
              image: headerImage,
              relativePosition: { x: 0, y: 0 },
            },
            {
              text: `${data.name} ${data.description}`,
              fontSize: 9,
              bold: true,
              relativePosition: { x: -20, y: 14 },
              alignment: 'right',
              color: '#fff',
            },
            {
              text: dayjs(dateRange?.[1]).format('MMMM, YYYY'),
              fontSize: 8,
              color: '#fff',
              relativePosition: { x: -20, y: 28 },
              alignment: 'right',
            },
          ],
        };
      }
      return false;
    } as unknown as Content,
    background: function (currentPage: number) {
      if (currentPage === 1) {
        return {
          image: frontPageImage,
          pageBreak: 'after',
          absolutePosition: { x: 0, y: 0 },
          width: 595,
          height: 842,
        };
      }
      return false;
    } as unknown as Content,
    content: [
      {
        stack: [
          {
            text: dayjs(dateRange?.[1]).format('MMMM, YYYY'),
            fontSize: 20,
            color: 'white',
            absolutePosition: { x: 88, y: 758 },
          },
          {
            text: documentId,
            fontSize: 13,
            color: 'white',
            bold: true,
            absolutePosition: { x: 384, y: 724 },
          },
          {
            text: 'IFU',
            fontSize: 13,
            color: 'white',
            bold: true,
            absolutePosition: { x: 384, y: 759 },
          },
          {
            text: '0',
            fontSize: 13,
            color: 'white',
            bold: true,
            absolutePosition: { x: 474, y: 759 },
          },
        ],
        margin: [0, 0, 0, 20],
        pageBreak: 'after',
      },
      {
        text: data.name,
        bold: true,
        color: '#565656',
        fontSize: 10,
      },
      {
        text: data.description,
        color: '#565656',
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },
      {
        columns: [
          {
            width: 90,
            image: await getImageFromName(`${data.name}.png`, 'asset'),
            fit: [80, 60],
          },
          {
            width: 90,
            table: {
              body: [
                [
                  {
                    borderColor: BORDER_GRAY,
                    innerHeight: 70,
                    innerWidth: 50,
                    stack: [
                      { text: 'HEALTH SCORE', color: '#565656', fontSize: 8 },
                      {
                        svg: buildGauge(data.health_score, 30),
                        height: 30,
                      },
                    ],
                    alignment: 'center',
                    margin: [10, 10, 10, 10],
                  },
                ],
              ],
            },
          },

          {
            stack: [
              {
                text: 'Total Condition Deviations',
                bold: true,
                color: '#565656',
                fontSize: 10,
              },
              {
                text: data.condition_deviation['total'],
                color: '#565656',
                fontSize: 9,
                margin: [0, 0, 0, 10],
              },
              {
                columns: [
                  {
                    stack: [
                      {
                        columns: [
                          {
                            svg: '<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 0.96875C3.25391 0.96875 0.21875 4.00391 0.21875 7.75C0.21875 11.4961 3.25391 14.5312 7 14.5312C10.7461 14.5312 13.7812 11.4961 13.7812 7.75C13.7812 4.00391 10.7461 0.96875 7 0.96875ZM9.51562 9.52734L8.96875 10.2109C8.88672 10.3203 8.77734 10.4023 8.61328 10.4023C8.53125 10.4023 8.42187 10.3477 8.36719 10.293L6.53516 8.92578C6.26172 8.73437 6.125 8.43359 6.125 8.07812L6.125 3.8125C6.125 3.59375 6.31641 3.375 6.5625 3.375H7.4375C7.65625 3.375 7.875 3.59375 7.875 3.8125L7.875 7.75L9.46094 8.92578C9.54297 9.00781 9.625 9.11719 9.625 9.25391C9.625 9.36328 9.57031 9.47266 9.51562 9.52734Z" fill="#565656"/></svg>',
                            width: 8,
                          },
                          {
                            text: 'Issued',
                            bold: true,
                            color: '#565656',
                            fontSize: 10,
                            margin: [5, 0, 0, 0],
                          },
                        ],
                      },
                      {
                        text: data.condition_deviation['issued'],
                        color: '#565656',
                        fontSize: 9,
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        columns: [
                          {
                            svg: '<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 14.75C7.95703 14.75 8.72266 13.9844 8.72266 13H5.25C5.25 13.9844 6.01562 14.75 7 14.75ZM12.8789 10.6758C12.3594 10.1016 11.3477 9.25391 11.3477 6.4375C11.3477 4.33203 9.87109 2.63672 7.84766 2.19922L7.84766 1.625C7.84766 1.16016 7.46484 0.75 7 0.75C6.50781 0.75 6.125 1.16016 6.125 1.625L6.125 2.19922C4.10156 2.63672 2.625 4.33203 2.625 6.4375C2.625 9.25391 1.61328 10.1016 1.09375 10.6758C0.929687 10.8398 0.847656 11.0586 0.875 11.25C0.875 11.7148 1.20312 12.125 1.75 12.125L12.2227 12.125C12.7695 12.125 13.0977 11.7148 13.125 11.25C13.125 11.0586 13.043 10.8398 12.8789 10.6758Z" fill="#565656"/></svg>',
                            width: 8,
                          },
                          {
                            text: 'Under Evaluation',
                            bold: true,
                            color: '#565656',
                            fontSize: 10,
                            margin: [5, 0, 0, 0],
                          },
                        ],
                      },
                      {
                        text: data.condition_deviation['under evaluation'],
                        color: '#565656',
                        fontSize: 9,
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        columns: [
                          {
                            svg: '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.73047 10.7812C6.00391 11.0547 6.46875 11.0547 6.74219 10.7812L14.7812 2.74219C15.0547 2.46875 15.0547 2.00391 14.7812 1.73047L13.7969 0.746094C13.5234 0.472656 13.0859 0.472656 12.8125 0.746094L6.25 7.30859L3.16016 4.24609C2.88672 3.97266 2.44922 3.97266 2.17578 4.24609L1.19141 5.23047C0.917969 5.50391 0.917969 5.96875 1.19141 6.24219L5.73047 10.7812Z" fill="#565656"/></svg>',
                            width: 8,
                          },
                          {
                            text: 'Completed',
                            bold: true,
                            color: '#565656',
                            fontSize: 10,
                            margin: [5, 0, 0, 0],
                          },
                        ],
                      },
                      {
                        text: data.condition_deviation['completed'],
                        color: '#565656',
                        fontSize: 9,
                      },
                    ],
                  },
                ],
              },
            ],
            margin: [10, 0],
          },
        ],
        margin: [0, 0, 0, 10],
      },
      JSON.parse(TABLE_HEADER_OBJ(true)),
    ],

    styles: {
      header: {
        fontSize: 18,
        bold: true,
      },
      sapNotificationHeader: {
        margin: [5, 5, 5, 5],
        bold: true,
        fontSize: 8,
      },
      system: {
        background: '#000',
      },
      link: {
        fontSize: 8,
        color: 'blue',
        decoration: 'underline',
      },
    },
  };

  const pageDescription: any[] = [];
  const newPageSubsystem: any[] = [];

  const tempContent: Content[] = [];
  const tempSapNotificationsContent: TableCell[][] = [];

  if (data.systems) {
    data.systems.forEach((system, i) => {
      const SYSTEM_ROW_OBJ = (isFirstPage?: boolean) => {
        return JSON.stringify({
          stack: [
            {
              table: {
                widths: isFirstPage ? [0, 134, 40, 319] : [0, 100, 40, 353],
                body: [
                  [
                    {
                      text: ' ',
                      fillColor: BLUE_BORDER,
                      borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER],
                      border: [true, true, true, true],
                      fontSize: 1,
                    },
                    {
                      borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER],
                      border: [true, true, false, false],
                      fillColor: '#E0E0E0',
                      alignment: 'left',
                      text: system.name,
                      fontSize: 9,
                      margin: [2, 4],
                    },
                    {
                      borderColor: ['', BLUE_BORDER, '', ''],
                      border: [false, true, false, false],
                      fillColor: '#E0E0E0',
                      alignment: 'left',
                      text: `${
                        system.health_score === 'NC'
                          ? '-'
                          : `${Math.round(Number(system.health_score))} %`
                      }`,
                      fontSize: 9,
                      margin: [0, 4],
                    },
                    {
                      borderColor: ['', BLUE_BORDER, BLUE_BORDER, ''],
                      border: [false, true, true, false],
                      fillColor: '#E0E0E0',
                      margin: [0, 4],
                      stack: [
                        {
                          svg: buildProgress(
                            system.health_score === 'NC'
                              ? 0
                              : Math.round(Number(system.health_score)),
                          ),
                          width: isFirstPage ? 319 : 350,
                        },
                      ],
                    },
                  ],
                ],
              },
              margin: [0, 0, 0, 0],
            },
            {
              table: {
                widths: [520],
                body: [
                  [
                    {
                      borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
                      border: [true, false, true, true],
                      alignment: 'left',
                      text: 'SubSystems',
                      fontSize: 7,
                      margin: [11, 0],
                      bold: true,
                    },
                  ],
                ],
              },
            },
          ],
        });
      };

      const SYSTEM_ROW_OBJ_LASTPAGE = () => {
        return JSON.stringify(
          Math.round(Number(system.health_score)) !== 100
            ? {
                stack: [
                  {
                    table: {
                      widths: [0, 100, 40, 353],
                      body: [
                        [
                          {
                            text: ' ',
                            fillColor: BLUE_BORDER,
                            borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER],
                            border: [true, true, true, true],
                            fontSize: 1,
                          },
                          {
                            borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER],
                            border: [true, true, false, false],
                            fillColor: '#E0E0E0',
                            alignment: 'left',
                            text: system.name,
                            fontSize: 9,
                            margin: [2, 4],
                          },
                          {
                            borderColor: ['', BLUE_BORDER, '', ''],
                            border: [false, true, false, false],
                            fillColor: '#E0E0E0',
                            alignment: 'left',
                            text: `${
                              system.health_score === 'NC'
                                ? '-'
                                : `${Math.round(Number(system.health_score))}%`
                            }`,
                            fontSize: 9,
                            margin: [0, 4],
                          },
                          {
                            borderColor: ['', BLUE_BORDER, BLUE_BORDER, ''],
                            border: [false, true, true, false],
                            fillColor: '#E0E0E0',
                            margin: [0, 4],
                            stack: [
                              {
                                svg: buildProgress(
                                  system.health_score === 'NC'
                                    ? 0
                                    : Math.round(Number(system.health_score)),
                                ),
                                width: 350,
                              },
                            ],
                          },
                        ],
                      ],
                    },
                    margin: [0, 10, 0, 0],
                  },
                  {
                    table: {
                      widths: [520],
                      body: [
                        [
                          {
                            borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
                            border: [true, false, true, true],
                            alignment: 'left',
                            text: 'SubSystems',
                            fontSize: 7,
                            margin: [11, 0],
                            bold: true,
                          },
                        ],
                      ],
                    },
                  },
                ],
              }
            : null,
        );
      };

      tempContent.push(JSON.parse(SYSTEM_ROW_OBJ_LASTPAGE()));

      pageDescription.push(JSON.parse(SYSTEM_ROW_OBJ(false)));

      (result.content as Content[]).push(JSON.parse(SYSTEM_ROW_OBJ(true)));

      const subsystems: any[] = [];

      const subsystemsPage2: any[] = [];

      system.list_subsystem.forEach((subsystem: types.SubSystem, s) => {
        const equipments: types.ListEquipment[] = [];
        const isLastSubsystem = s === system.list_subsystem.length - 1;

        if (subsystem.list_equipments) {
          subsystem.list_equipments.forEach((equipment) => {
            const hasDiagnostic = equipment.parameters.filter((item) => item.diagnostic.length);
            if (hasDiagnostic.length) {
              equipments.push(equipment);
            }
          });
        }

        const SUBSYSTEM_ROW_OBJ = (isBorderBottom: boolean, isFirstPage: boolean) => {
          return JSON.stringify([
            {
              borderColor:
                isLastSubsystem || isBorderBottom
                  ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                  : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
              border: !isFirstPage ? [true, false, false, false] : [true, false, false, true],
              alignment: 'left',
              text: `- ${subsystem.name}`,
              margin: isFirstPage ? [11, 2, 0, 0] : [3, 2, 0, 0],
              fontSize: 9,
              linkToDestination: `${system.name}-equipment-${subsystem.name}`,
              color: 'blue',
              decoration: 'underline',
            },
            {
              border: !isFirstPage ? [false, false, false, false] : [false, false, false, true],
              borderColor:
                isLastSubsystem || isBorderBottom
                  ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                  : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
              alignment: 'left',
              text: `${
                subsystem.health_score === 'NC'
                  ? '-'
                  : `${Math.round(Number(subsystem.health_score))}%`
              }`,
              margin: [13, 2, 0, 2],
              fontSize: 9,
            },
            {
              borderColor:
                isLastSubsystem || isBorderBottom
                  ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                  : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
              border: !isFirstPage ? [false, false, true, false] : [false, false, true, true],
              margin: [0, 4, 0, 0],
              stack: [
                {
                  svg: buildProgress(
                    subsystem.health_score === 'NC'
                      ? 0
                      : Math.round(Number(subsystem.health_score)),
                  ),
                  width: 316,
                },
              ],
            },
          ]);
        };

        const SUBSYSTEM_LASTPAGE_ROW_OBJ = () => {
          return JSON.stringify(
            Math.round(Number(subsystem.health_score)) !== 100 && equipments.length
              ? [
                  {
                    borderColor: isLastSubsystem
                      ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                      : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
                    border:
                      equipments.length > 0
                        ? [true, false, false, false]
                        : [true, false, false, true],
                    alignment: 'left',
                    text: `- ${subsystem.name}`,
                    margin: [11, 2, 0, 0],
                    fontSize: 9,
                  },
                  {
                    border:
                      equipments.length > 0
                        ? [false, false, false, false]
                        : [false, false, false, true],
                    borderColor: isLastSubsystem
                      ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                      : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
                    alignment: 'left',
                    text: `${
                      subsystem.health_score === 'NC'
                        ? '-'
                        : `${Math.round(Number(subsystem.health_score))}%`
                    }`,
                    margin: [0, 2, 0, 2],
                    fontSize: 9,
                  },
                  {
                    borderColor: isLastSubsystem
                      ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                      : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#E0E0E0'],
                    border:
                      equipments.length > 0
                        ? [false, false, true, false]
                        : [false, false, true, true],
                    margin: [0, 4, 0, 0],
                    stack: [
                      {
                        svg: buildProgress(
                          subsystem.health_score === 'NC'
                            ? 0
                            : Math.round(Number(subsystem.health_score)),
                        ),
                        width: 350,
                      },
                    ],
                  },
                ]
              : [],
          );
        };

        tempContent.push({
          table: {
            widths: [109, 40, 353],
            body: [JSON.parse(SUBSYSTEM_LASTPAGE_ROW_OBJ())],
          },
        });

        equipments.forEach((equipment, indxEquipment) => {
          const diagnostics = equipment.parameters.flatMap((item) => item.diagnostic);
          const hasDiagnostic = equipment.parameters.filter((item) => item.diagnostic.length);

          if (Math.round(Number(equipment.health_score)) !== 100 && externalIdReferences) {
            tempContent.push({
              table: {
                widths: [520],
                body: [
                  [
                    {
                      borderColor: [BLUE_BORDER, '', BLUE_BORDER, ''],
                      border: [true, false, true, false],
                      stack: [
                        {
                          columns: [
                            {
                              width: 118,
                              text: equipment.name,
                              link: `${import.meta.env.VITE_WEB_URL}/asset/${
                                externalIdReferences[equipment.web_id].id
                              }/overview`,
                              style: 'link',
                              margin: [11, 0, 0, 0],
                              fontSize: 7,
                            },
                            {
                              width: 48,
                              text:
                                equipment.health_score === 'NC'
                                  ? '-'
                                  : `${Math.round(Number(equipment.health_score))}%`,
                              fontSize: 9,
                              margin: [0, 0, 0, 0],
                            },
                          ],
                          margin: [0, 10],
                        },
                      ],
                    },
                  ],
                ],
              },
            });
          }

          hasDiagnostic.forEach((parameter, idx) => {
            if (Math.round(Number(equipment.health_score)) !== 100) {
              tempContent.push({
                table: {
                  body: [
                    [
                      {
                        border: [
                          true,
                          false,
                          true,
                          !!(
                            idx === hasDiagnostic.length - 1 &&
                            indxEquipment === equipments.length - 1
                          ),
                        ],
                        borderColor: [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER],

                        stack: [
                          {
                            columns: [
                              {
                                margin: [12, 4, 0, 0],
                                text: parameter.name,
                                bold: true,
                                fontSize: 9,
                                width: 110,
                              },
                              {
                                canvas: [
                                  {
                                    type: 'line',
                                    x1: 0,
                                    y1: 10,
                                    x2: 370,
                                    y2: 10,
                                    lineWidth: 0.5,
                                  },
                                ],
                                width: 240,
                              },
                              {
                                alignment: 'right',
                                svg: `<svg width="15" height="15"><rect width="15" height="15" fill="${
                                  SeverityStatusColorMap[parameter.condition_evaluation[0]?.name] ||
                                  '#63DC76'
                                }" /></svg>`,
                                margin: [0, 3, 3, 0],
                              },
                            ],
                            margin: [0, 0, 0, 15],
                          },
                          {
                            table: {
                              body: parameter.diagnostic.map((item, v) => {
                                return [
                                  {
                                    border: [
                                      false,
                                      false,
                                      false,
                                      idx === hasDiagnostic.length - 1 &&
                                        v === parameter.diagnostic.length - 1,
                                    ],
                                    stack: [
                                      {
                                        columns: [
                                          {
                                            stack: [
                                              {
                                                text: 'Diagnostic',
                                                bold: true,
                                                id: `${item.name}-${parameter.name}-${v}-${equipment.name}`,
                                                color: '#565656',
                                                fontSize: 7,
                                              },
                                              {
                                                text: item.condition_diagnostic,
                                                color: '#565656',
                                                fontSize: 9,
                                              },
                                            ],
                                          },
                                          {
                                            alignment: 'right',
                                            svg: `<svg width="15" height="15"><rect width="15" height="15" fill="${
                                              SeverityStatusColorMap[
                                                parameter.condition_evaluation[0]?.name
                                              ] || '#63DC76'
                                            }" /></svg>`,
                                          },
                                        ],
                                        margin: [0, 10, 0, 0],
                                      },
                                      {
                                        columns: [
                                          {
                                            width: 255,
                                            stack: [
                                              {
                                                text: 'Detailing',
                                                bold: true,
                                                color: '#565656',
                                                fontSize: 7,
                                              },
                                              {
                                                text: item.detailing,
                                                color: '#565656',
                                                fontSize: 9,
                                              },
                                            ],
                                            margin: [0, 0, 10, 12],
                                          },
                                          {
                                            width: 255,
                                            stack: [
                                              {
                                                text: 'Action',
                                                bold: true,
                                                color: '#565656',
                                                fontSize: 7,
                                              },
                                              {
                                                text: item.action,
                                                color: '#565656',
                                                fontSize: 9,
                                              },
                                            ],
                                          },
                                        ],
                                        margin: [0, 10, 0, 12],
                                      },

                                      {
                                        columns: [
                                          {
                                            columns: [
                                              {
                                                stack: [
                                                  {
                                                    text: 'Start Date',
                                                    bold: true,
                                                    color: '#565656',
                                                    fontSize: 7,
                                                  },
                                                  {
                                                    text: dayjs(item.start_time).format(
                                                      'DD/MM/YYYY',
                                                    ),
                                                    color: '#565656',
                                                    fontSize: 9,
                                                  },
                                                ],
                                              },
                                              {
                                                stack: [
                                                  {
                                                    text: 'End Date',
                                                    bold: true,
                                                    color: '#565656',
                                                    fontSize: 7,
                                                  },
                                                  {
                                                    text:
                                                      item.end_time ===
                                                      'Fri, 31 Dec 9999 23:59:59 GMT'
                                                        ? '-'
                                                        : dayjs(item.end_time).format('DD/MM/YYYY'),
                                                    color: '#565656',
                                                    fontSize: 9,
                                                  },
                                                ],
                                              },
                                              {
                                                stack: [
                                                  {
                                                    text: 'Downtime avoided',
                                                    bold: true,
                                                    color: '#565656',
                                                    fontSize: 7,
                                                    margin: [0, 0, 10, 0],
                                                  },
                                                  {
                                                    text: item.downtime_avoided,
                                                    color: '#565656',
                                                    fontSize: 9,
                                                    margin: [0, 0, 10, 0],
                                                  },
                                                ],
                                              },
                                              {
                                                stack: [
                                                  {
                                                    text: 'Action Status',
                                                    bold: true,
                                                    color: '#565656',
                                                    fontSize: 7,
                                                  },
                                                  {
                                                    text: item.action_status,
                                                    color: '#565656',
                                                    fontSize: 9,
                                                  },
                                                ],
                                              },
                                            ],
                                            margin: [0, 0, 0, 10],
                                          },
                                          {
                                            columns: [
                                              {
                                                stack: [
                                                  {
                                                    text: 'SAP notification',
                                                    bold: true,
                                                    color: '#565656',
                                                    fontSize: 7,
                                                  },
                                                  {
                                                    text: item.sap_notification,
                                                    color: '#565656',
                                                    fontSize: 9,
                                                  },
                                                ],
                                              },
                                              {
                                                stack: [
                                                  {
                                                    text: 'SAP notification status',
                                                    bold: true,
                                                    color: '#565656',
                                                    fontSize: 7,
                                                  },
                                                  {
                                                    text: item.sap_notification_status,
                                                    color: '#565656',
                                                    fontSize: 9,
                                                  },
                                                ],
                                              },
                                            ],
                                            margin: [0, 0, 0, 10],
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                ];
                              }),
                            },
                            layout: {
                              hLineStyle(i: number) {
                                return { dash: { length: 1, space: 2 } };
                              },
                            },
                          },
                        ],
                      },
                    ],
                  ],
                },
                margin: [0, 0, 0, 0],
              });
            }
          });
          const diagnosticsWithSapNotifications = getDiagnosticsWithSapNotifications(diagnostics);

          if (diagnosticsWithSapNotifications.length > 0) {
            const equipmentSapNotificationLine = buildEquipmentSapNotificationLine(
              diagnosticsWithSapNotifications,
              equipment.name,
            );
            if (tempSapNotificationsContent.length !== 0) {
              tempSapNotificationsContent.push([DividerLine()]);
            }
            tempSapNotificationsContent.push(equipmentSapNotificationLine);
          }
        });
        subsystemsPage2.push({
          table: {
            body: [
              [
                {
                  borderColor: isLastSubsystem
                    ? [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, BLUE_BORDER]
                    : [BLUE_BORDER, BLUE_BORDER, BLUE_BORDER, '#262626'],
                  border: [true, false, true, true],
                  stack: [
                    {
                      columns: [
                        {
                          width: 115,
                          text: `- ${subsystem.name}`,
                          fontSize: 9,
                          margin: [9, 4, 0, 4],
                          id: `${system.name}-equipment-${subsystem.name}`,
                        },
                        {
                          width: 53,
                          text: `${
                            subsystem.health_score === 'NC'
                              ? '-'
                              : `${Math.round(Number(subsystem.health_score))}%`
                          }`,
                          fontSize: 9,
                          margin: [3, 4, 0, 4],
                        },
                        {
                          width: 352,
                          stack: [
                            {
                              svg: buildProgress(
                                subsystem.health_score === 'NC'
                                  ? 0
                                  : Math.round(Number(subsystem.health_score)),
                              ),
                              width: 349,
                              margin: [0, 4, 0, 4],
                            },
                          ],
                        },
                      ],
                      margin: [0, 4, 0, 4],
                    },
                    subsystem.list_equipments?.map((item) => ({
                      stack: [
                        {
                          columns: [
                            {
                              width: 118,
                              text: item.name,
                              link: `${import.meta.env.VITE_WEB_URL}/asset/${
                                (externalIdReferences as Record<string, BaseAsset>)[item.web_id].id
                              }/overview`,
                              style: 'link',
                              margin: [12, 0, 0, 0],
                              fontSize: 7,
                            },
                            {
                              width: 50,
                              text:
                                item.health_score === 'NC'
                                  ? '-'
                                  : `${Math.round(Number(item.health_score))}%`,
                              fontSize: 8,
                            },
                          ],
                          margin: [0, 10],
                        },
                        {
                          columns: item.parameters.map((parameter, i) => ({
                            width: 129,
                            margin: i === 3 ? [12, 0, 0, 0] : [12, 0, 0, 0],
                            table: {
                              widths: '*',
                              body: [
                                [
                                  {
                                    borderColor: BORDER_GRAY,
                                    stack: [
                                      {
                                        columns: [
                                          {
                                            width: 90,
                                            text: parameter.name,
                                            bold: true,
                                            fontSize: 8,
                                          },
                                          {
                                            alignment: 'right',
                                            svg: `<svg width="15" height="15"><rect width="15" height="15" fill="${
                                              SeverityStatusColorMap[
                                                parameter.condition_evaluation[0]?.name
                                              ] || '#63DC76'
                                            }" /></svg>`,
                                          },
                                        ],
                                      },
                                      getPDFDiagnostic(parameter, item),
                                    ],
                                  },
                                ],
                              ],
                            },
                          })),
                          margin: [0, 0, 0, 10],
                        },
                      ],
                    })),
                  ],
                },
              ],
            ],
          },
          layout: {
            hLineStyle: () => {
              if (isLastSubsystem) {
                return null;
              }
              return { dash: { length: 1, space: 2 } };
            },
          },
          margin: s === system.list_subsystem.length - 1 ? [0, 0, 0, 10] : [0, 0, 0, 0],
        });
        subsystems.push(JSON.parse(SUBSYSTEM_ROW_OBJ(false, true)));
      });

      const itens: Content = {
        table: {
          widths: [130, 56, 316],
          body: subsystems,
        },
        margin: [0, 0, 0, 10],
      };
      const r: Content = data.systems?.length === i + 1 ? { ...itens, pageBreak: 'after' } : itens;

      newPageSubsystem.push(subsystemsPage2);

      (result.content as Content[]).push(r);
    });
  }

  (result.content as Content[]).push(JSON.parse(TABLE_HEADER_OBJ(false)));

  pageDescription.forEach((item, i) => {
    (result.content as Content[]).push({ ...item });
    newPageSubsystem[i].forEach((subitem: any, indexSub: number) => {
      let b = subitem;
      const lastItem =
        i === pageDescription.length - 1 && newPageSubsystem[i].length - 1 === indexSub;
      if (lastItem) {
        b = { ...subitem, pageBreak: 'after' };
      }
      (result.content as Content[]).push(b);
      if (lastItem) {
        (result.content as Content[]).push(JSON.parse(legendAsString));
      }
    });
  });

  tempContent.forEach((item) => {
    (result.content as Content[]).push(item);
  });

  (result.content as Content[]).push(
    buildTemplateSapNotificationsPage(tempSapNotificationsContent),
  );
  return new Promise((resolve, _reject) => {
    resolve(result);
  });
};
